import { useSelector } from 'react-redux';

import locale from '../../utils/localization.json';
import {
    IcAdd,
    IcAddFill,
    IcModalBack,
    IcModalClose,
    IcModalConfirm,
    IcRemove,
    IcRemoveFill,
} from '../../share/icons';
import { errorMessage } from '../../utils/message';

const AnalyzeConfigModal = ({
    min,
    max,
    filter,
    ranges,
    setMin,
    setMax,
    setFilter,
    setRanges,
    setShowConfig,
    setShowDistance,
}) => {
    const lang = useSelector((state) => state.language);
    return (
        <>
            <div className="justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none font-poppins">
                <div className="relative w-auto my-6 mx-auto max-w-3xl">
                    {/*container*/}
                    <div className="border-0 rounded-lg relative flex flex-col w-full bg-modelic-gray-900 outline-none focus:outline-none">
                        {/*header*/}
                        <div className="flex items-start justify-between p-4 rounded-t">
                            <h3 className="text-2xl font-medium text-modelic-gray-100">
                                {locale[lang].config}
                            </h3>
                            <button
                                className="p-1 ml-auto bg-transparent border-0 text-modelic-gray-100 float-right text-3xl leading-none font-semibold outline-none focus:outline-none"
                                onClick={() => setShowConfig(false)}
                            >
                                <span className="bg-transparent text-modelic-gray-100 h-6 w-6 text-2xl block outline-none focus:outline-none">
                                    {IcModalClose}
                                </span>
                            </button>
                        </div>
                        {/*content*/}
                        <div className="flex">
                            <div className="p-6 flex-auto text-modelic-gray-400">
                                <div>{locale[lang].distanceMinMax}</div>
                                <div className="text-xs mb-2">
                                    ({locale[lang].distanceMinMaxRange})
                                </div>
                                <div className="flex text-lg items-center mb-2">
                                    {locale[lang].min} (cm)
                                    <input
                                        id="analysis-min"
                                        className="h-8 ml-2 p-2 w-20 outline-0 rounded border border-modelic-gray-700 bg-modelic-gray-900 text-modelic-gray-400 text-base font-medium font-poppins leading-8 self-center"
                                        type="number"
                                        onChange={(e) =>
                                            setMin(Number(e.target.value))
                                        }
                                        onKeyDown={(e) => e.preventDefault()}
                                        value={min}
                                        min={1}
                                        max={max - 1}
                                    />
                                </div>
                                <div className="flex text-lg items-center">
                                    {locale[lang].max} (cm)
                                    <input
                                        id="analysis-max"
                                        className="h-8 ml-2 p-2 w-20 outline-0 rounded border border-modelic-gray-700 bg-modelic-gray-900 text-modelic-gray-400 text-base font-medium font-poppins leading-8 self-center"
                                        type="number"
                                        onChange={(e) =>
                                            setMax(Number(e.target.value))
                                        }
                                        onKeyDown={(e) => e.preventDefault()}
                                        value={max}
                                        min={min + 1}
                                        max={20}
                                    />
                                </div>
                                <div className="w-full h-0 my-4 border border-modelic-gray-800"></div>
                                <div>{locale[lang].distanceFilter}</div>
                                <div
                                    className={`flex text-lg items-center my-2 text-modelic-gray-${
                                        filter.type === 0 ? 400 : 600
                                    }`}
                                >
                                    <input
                                        id="radio-analysis-min"
                                        className="w-4 h-4 mr-2 text-modelic-primary-main bg-modelic-gray-100 border-modelic-gray-300 rounded-sm"
                                        type="radio"
                                        name="radio-filter"
                                        onChange={(e) =>
                                            e.target.checked &&
                                            setFilter({
                                                ...filter,
                                                type: 0,
                                            })
                                        }
                                        checked={filter.type === 0}
                                    />
                                    {locale[lang].distanceFilterAll}
                                </div>
                                <div
                                    className={`flex text-lg items-center my-2 text-modelic-gray-${
                                        filter.type === 1 ? 400 : 600
                                    }`}
                                >
                                    <input
                                        id="radio-analysis-min"
                                        className="w-4 h-4 mr-2 text-modelic-primary-main bg-modelic-gray-100 border-modelic-gray-300 rounded-sm"
                                        type="radio"
                                        name="radio-filter"
                                        onChange={(e) =>
                                            e.target.checked &&
                                            setFilter({
                                                type: 1,
                                                value: {
                                                    ...filter.value,
                                                    min: filter.value.min || 1,
                                                },
                                            })
                                        }
                                        checked={filter.type === 1}
                                    />
                                    {locale[lang].distanceFilterMin} (cm)
                                    <input
                                        disabled={filter.type !== 1}
                                        id="analysis-min"
                                        className="h-8 ml-2 p-2 w-20 outline-0 rounded border border-modelic-gray-700 bg-modelic-gray-900 text-base font-medium font-poppins leading-8 self-center"
                                        type="number"
                                        onChange={(e) =>
                                            setFilter({
                                                type: 1,
                                                value: {
                                                    ...filter.value,
                                                    min: Number(e.target.value),
                                                },
                                            })
                                        }
                                        onKeyDown={(e) => e.preventDefault()}
                                        value={filter.value.min}
                                        min={1}
                                        max={20}
                                    />
                                </div>
                                <div
                                    className={`flex text-lg items-center my-2 text-modelic-gray-${
                                        filter.type === 2 ? 400 : 600
                                    }`}
                                >
                                    <input
                                        id="radio-analysis-between"
                                        className="w-4 h-4 mr-2 text-modelic-primary-main bg-modelic-gray-100 border-modelic-gray-300 rounded-sm"
                                        type="radio"
                                        name="radio-filter"
                                        onChange={(e) =>
                                            e.target.checked &&
                                            setFilter({
                                                type: 2,
                                                value: {
                                                    ...filter.value,
                                                    down:
                                                        filter.value.down || 0,
                                                    up: filter.value.up || 20,
                                                },
                                            })
                                        }
                                        checked={filter.type === 2}
                                    />
                                    {locale[lang].distanceFilterBetween} (cm)
                                    <input
                                        disabled={filter.type !== 2}
                                        id="analysis-min"
                                        className="h-8 ml-2 p-2 w-20 outline-0 rounded border border-modelic-gray-700 bg-modelic-gray-900 text-base font-medium font-poppins leading-8 self-center"
                                        type="number"
                                        onChange={(e) =>
                                            setFilter({
                                                type: 2,
                                                value: {
                                                    ...filter.value,
                                                    down: Number(
                                                        e.target.value
                                                    ),
                                                },
                                            })
                                        }
                                        onKeyDown={(e) => e.preventDefault()}
                                        value={filter.value.down}
                                        min={0}
                                        max={filter.value.up - 1}
                                    />
                                    -
                                    <input
                                        disabled={filter.type !== 2}
                                        id="analysis-min"
                                        className="h-8 p-2 w-20 outline-0 rounded border border-modelic-gray-700 bg-modelic-gray-900 text-base font-medium font-poppins leading-8 self-center"
                                        type="number"
                                        onChange={(e) =>
                                            setFilter({
                                                type: 2,
                                                value: {
                                                    ...filter.value,
                                                    up: Number(e.target.value),
                                                },
                                            })
                                        }
                                        onKeyDown={(e) => e.preventDefault()}
                                        value={filter.value.up}
                                        min={filter.value.down + 1}
                                        max={20}
                                    />
                                </div>
                                <div
                                    className={`flex text-lg items-center my-2 text-modelic-gray-${
                                        filter.type === 3 ? 400 : 600
                                    }`}
                                >
                                    <input
                                        id="radio-analysis-max"
                                        className="w-4 h-4 mr-2 text-modelic-primary-main bg-modelic-gray-100 border-modelic-gray-300 rounded-sm"
                                        type="radio"
                                        name="radio-filter"
                                        onChange={(e) =>
                                            e.target.checked &&
                                            setFilter({
                                                type: 3,
                                                value: {
                                                    ...filter.value,
                                                    max: filter.value.max || 20,
                                                },
                                            })
                                        }
                                        checked={filter.type === 3}
                                    />
                                    {locale[lang].distanceFilterMax} (cm)
                                    <input
                                        disabled={filter.type !== 3}
                                        id="analysis-min"
                                        className="h-8 ml-2 p-2 w-20 outline-0 rounded border border-modelic-gray-700 bg-modelic-gray-900 text-base font-medium font-poppins leading-8 self-center"
                                        type="number"
                                        onChange={(e) =>
                                            setFilter({
                                                type: 3,
                                                value: {
                                                    ...filter.value,
                                                    max: Number(e.target.value),
                                                },
                                            })
                                        }
                                        onKeyDown={(e) => e.preventDefault()}
                                        value={filter.value.max}
                                        min={1}
                                        max={20}
                                    />
                                </div>
                            </div>
                            <div className="p-6 flex-auto text-modelic-gray-400">
                                <div>{locale[lang].distancePercentage}</div>
                                <div className="text-xs mb-2">
                                    ({locale[lang].distancePercentageRange})
                                </div>
                                {ranges.map(({ min, max }, index) => (
                                    <div
                                        key={`ranges-${index}`}
                                        className="flex text-lg align-middle mb-2"
                                    >
                                        <input
                                            className="h-8 p-2 w-20 outline-0 rounded border border-modelic-gray-700 bg-modelic-gray-900 text-modelic-gray-400 text-base font-medium font-poppins leading-8 self-center"
                                            type="number"
                                            onChange={(e) =>
                                                setRanges((pre) => {
                                                    const updated = [...pre];
                                                    updated[index].min = Number(
                                                        e.target.value
                                                    );
                                                    updated[
                                                        index
                                                    ].title = `${updated[index].min}-${updated[index].max}`;
                                                    return updated;
                                                })
                                            }
                                            onKeyDown={(e) =>
                                                e.preventDefault()
                                            }
                                            value={min}
                                            min={
                                                ranges[index - 1]
                                                    ? ranges[index - 1].max
                                                    : 0
                                            }
                                            max={max}
                                            step={0.1}
                                        />
                                        -
                                        <input
                                            className="h-8 p-2 w-20 outline-0 rounded border border-modelic-gray-700 bg-modelic-gray-900 text-modelic-gray-400 text-base font-medium font-poppins leading-8 self-center"
                                            type="number"
                                            onChange={(e) =>
                                                setRanges((pre) => {
                                                    const updated = [...pre];
                                                    updated[index].max = Number(
                                                        e.target.value
                                                    );
                                                    updated[
                                                        index
                                                    ].title = `${updated[index].min}-${updated[index].max}`;
                                                    return updated;
                                                })
                                            }
                                            onKeyDown={(e) =>
                                                e.preventDefault()
                                            }
                                            value={max}
                                            min={min}
                                            max={
                                                ranges[index + 1]
                                                    ? ranges[index + 1].min
                                                    : 20
                                            }
                                            step={0.1}
                                        />
                                        {index > 0 && (
                                            <button
                                                className="ml-2 group"
                                                onClick={() => {
                                                    const updated = [...ranges];
                                                    updated.splice(index, 1);
                                                    setRanges(updated);
                                                }}
                                            >
                                                {IcRemove}
                                                {IcRemoveFill}
                                            </button>
                                        )}
                                    </div>
                                ))}
                                {ranges.length < 10 && (
                                    <div className="flex text-sm align-middle">
                                        {locale[lang].add}
                                        <button
                                            className="ml-2 group"
                                            onClick={() => {
                                                if (
                                                    ranges[ranges.length - 1]
                                                        .max >= 20
                                                )
                                                    errorMessage(
                                                        locale[lang]
                                                            .distancePercentageRangeMax
                                                    );
                                                else
                                                    setRanges([
                                                        ...ranges,
                                                        {
                                                            min: ranges[
                                                                ranges.length -
                                                                    1
                                                            ].max,
                                                            max: 20,
                                                            title: `${
                                                                ranges[
                                                                    ranges.length -
                                                                        1
                                                                ].max
                                                            }-20`,
                                                        },
                                                    ]);
                                            }}
                                        >
                                            {IcAdd}
                                            {IcAddFill}
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="p-4 flex justify-center items-center space-x-8">
                            <button
                                className="flex justify-center items-center space-x-3 px-6 py-3 capitalize rounded hover:scale-105 text-modelic-primary-main"
                                onClick={() => {
                                    setShowConfig(false);
                                    setShowDistance(false);
                                }}
                            >
                                <span>{IcModalBack}</span>
                                <span>{locale[lang].back}</span>
                            </button>

                            <button
                                className="flex justify-center items-center space-x-3 px-6 py-3 capitalize rounded hover:scale-105 bg-modelic-primary-main text-modelic-gray-100"
                                onClick={() => {
                                    setShowDistance(true);
                                    setShowConfig(false);
                                }}
                            >
                                <span>{locale[lang].confirm}</span>
                                <span>{IcModalConfirm}</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <div className="opacity-30 fixed inset-0 z-40 bg-black"></div>
        </>
    );
};

export default AnalyzeConfigModal;
